<template>
  <div class="overflow-hidden">
    <div
      v-if="!isEditorLoaded"
      class="flex items-center justify-center rounded"
      :style="{
        backgroundColor: themeVars.primary6,
        color: themeVars.text3,
        height: `${height}px`,
      }"
    >
      <div class="flex items-center gap-2">
        <CIcon icon="code" size="16px" />
        {{ t('canvasUI.CMonacoEditor.loadingCodeEditor') }}
      </div>
    </div>
    <div v-show="isEditorLoaded" ref="editorContainer" class="w-full" :style="{ height: `${height}px` }" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import type MonacoEditor from 'monaco-editor'

const props = withDefaults(
  defineProps<{
    modelValue: string
    height?: number
    readonly?: boolean
    language?: string
  }>(),
  {
    height: 400,
    readonly: false,
    language: 'html',
  },
)

const emit = defineEmits<{
  'update:modelValue': [string]
  focus: []
  blur: []
  save: []
}>()

const { t } = useI18n()
const themeVars = useThemeVars()
const editorContainer = ref<HTMLElement>()
const isEditorLoaded = ref(false)
let monacoEditor: MonacoEditor.editor.IStandaloneCodeEditor | null | undefined = null
let monaco: typeof window.monaco | null = null

// Global flag to track Monaco loading state
const MONACO_LOADING_KEY = '__MONACO_LOADING__'
const MONACO_LOADER_ID = 'monaco-loader-script'

const loadMonaco = async () => {
  try {
    // If Monaco is already loaded, use it
    if (window.monaco) {
      monaco = window.monaco
    }
    // Wait if Monaco is currently being loaded by another instance
    else if (window[MONACO_LOADING_KEY]) {
      await window[MONACO_LOADING_KEY]
      monaco = window.monaco
    }
    // Load Monaco for the first time
    else {
      // Set loading flag
      const loadingPromise = (async () => {
        // Check if loader script already exists
        if (!document.getElementById(MONACO_LOADER_ID)) {
          // Load Monaco CSS (check if not already present)
          if (!document.querySelector('link[href*="monaco-editor"][href*="editor.main.css"]')) {
            const cssLink = document.createElement('link')
            cssLink.rel = 'stylesheet'
            cssLink.href = 'https://cdn.jsdelivr.net/npm/monaco-editor@0.52.2/min/vs/editor/editor.main.css'
            document.head.appendChild(cssLink)
          }

          // Load Monaco JS
          const script = document.createElement('script')
          script.id = MONACO_LOADER_ID
          script.src = 'https://cdn.jsdelivr.net/npm/monaco-editor@0.52.2/min/vs/loader.js'

          await new Promise((resolve, reject) => {
            script.onload = resolve
            script.onerror = reject
            document.head.appendChild(script)
          })
        }

        // Configure AMD loader (only if not already configured)
        if (!window.require || !window.require.config) {
          await new Promise((resolve) => setTimeout(resolve, 100))
        }

        if (window.require && !window.monaco) {
          window.require.config({
            paths: {
              vs: 'https://cdn.jsdelivr.net/npm/monaco-editor@0.52.2/min/vs',
            },
          })

          // Load Monaco
          await new Promise((resolve) => {
            window.require(['vs/editor/editor.main'], () => {
              resolve(undefined)
            })
          })
        }
      })()

      window[MONACO_LOADING_KEY] = loadingPromise
      await loadingPromise

      monaco = window.monaco as any
      delete window[MONACO_LOADING_KEY]
    }

    if (!monaco) return

    // Configure Monaco for HTML
    monaco.languages.html.htmlDefaults.setOptions({
      format: {
        contentUnformatted: 'none',
        tabSize: 2,
        insertSpaces: true,
        wrapLineLength: 120,
        unformatted:
          'a, abbr, acronym, b, bdo, big, br, button, cite, code, dfn, em, i, kbd, label, map, object, q, samp, small, span, strong, sub, sup, textarea, tt, var',
        indentInnerHtml: false,
        preserveNewLines: true,
        maxPreserveNewLines: undefined,
        indentHandlebars: false,
        endWithNewline: false,
        extraLiners: 'head, body, /html',
        wrapAttributes: 'auto',
      },
      suggest: {
        html5: true,
      },
    })

    // Register SOQL language (Salesforce Object Query Language) - only once
    const registeredLanguages = monaco?.languages.getLanguages().map((l) => l.id) || []
    if (!registeredLanguages.includes('soql')) {
      monaco?.languages.register({ id: 'soql' })
      monaco?.languages.setMonarchTokensProvider('soql', {
        defaultToken: '',
        tokenPostfix: '.soql',
        ignoreCase: true,
        keywords: [
          'SELECT',
          'FROM',
          'WHERE',
          'ORDER BY',
          'GROUP BY',
          'HAVING',
          'LIMIT',
          'OFFSET',
          'WITH',
          'AND',
          'OR',
          'NOT',
          'IN',
          'LIKE',
          'INCLUDES',
          'EXCLUDES',
          'ASC',
          'DESC',
          'NULLS FIRST',
          'NULLS LAST',
          'FOR VIEW',
          'FOR REFERENCE',
          'UPDATE TRACKING',
          'UPDATE VIEWSTAT',
          'TYPEOF',
          'END',
          'WHEN',
          'THEN',
          'ELSE',
          'USING SCOPE',
          'FOR UPDATE',
        ],
        operators: ['=', '!=', '<', '>', '<=', '>=', '<>'],
        builtinFunctions: [
          'COUNT',
          'SUM',
          'AVG',
          'MIN',
          'MAX',
          'COUNT_DISTINCT',
          'CALENDAR_MONTH',
          'CALENDAR_QUARTER',
          'CALENDAR_YEAR',
          'DAY_IN_MONTH',
          'DAY_IN_WEEK',
          'DAY_IN_YEAR',
          'DAY_ONLY',
          'FISCAL_MONTH',
          'FISCAL_QUARTER',
          'FISCAL_YEAR',
          'HOUR_IN_DAY',
          'WEEK_IN_MONTH',
          'WEEK_IN_YEAR',
          'convertTimezone',
          'convertCurrency',
          'toLabel',
          'FORMAT',
          'GROUPING',
          'ROLLUP',
          'CUBE',
        ],
        tokenizer: {
          root: [
            { include: '@whitespace' },
            { include: '@numbers' },
            { include: '@strings' },
            { include: '@complexIdentifiers' },
            [/[;,.]/, 'delimiter'],
            [/[()]/, '@brackets'],
            [
              /[\w@#$]+/,
              {
                cases: {
                  '@keywords': 'keyword',
                  '@operators': 'operator',
                  '@builtinFunctions': 'predefined',
                  '@default': 'identifier',
                },
              },
            ],
            [/[<>=!%&+\-*/|~^]/, 'operator'],
          ],
          whitespace: [[/\s+/, 'white']],
          numbers: [
            [/0[xX][0-9a-fA-F]*/, 'number'],
            [/[$][+-]*\d*(\.\d*)?/, 'number'],
            [/((\d+(\.\d*)?)|(\.\d+))([eE][-+]?\d+)?/, 'number'],
          ],
          strings: [[/'/, { token: 'string.quote', bracket: '@open', next: '@string' }]],
          string: [
            [/[^\\']+/, 'string'],
            [/\\./, 'string.escape'],
            [/'/, { token: 'string.quote', bracket: '@close', next: '@pop' }],
          ],
          complexIdentifiers: [[/`/, { token: 'identifier.quote', bracket: '@open', next: '@quotedIdentifier' }]],
          quotedIdentifier: [
            [/[^`]+/, 'identifier'],
            [/`/, { token: 'identifier.quote', bracket: '@close', next: '@pop' }],
          ],
        },
      })
    }

    // Register SOSL language (Salesforce Object Search Language) - only once
    if (!registeredLanguages.includes('sosl')) {
      monaco?.languages.register({ id: 'sosl' })
      monaco?.languages.setMonarchTokensProvider('sosl', {
        defaultToken: '',
        tokenPostfix: '.sosl',
        ignoreCase: true,
        keywords: [
          'FIND',
          'IN',
          'ALL FIELDS',
          'EMAIL FIELDS',
          'NAME FIELDS',
          'PHONE FIELDS',
          'SIDEBAR FIELDS',
          'RETURNING',
          'WHERE',
          'ORDER BY',
          'LIMIT',
          'OFFSET',
          'WITH',
          'NETWORK',
          'SNIPPET',
          'TARGET_LENGTH',
          'DIVISION',
          'DATA CATEGORY',
          'AT',
          'ABOVE',
          'BELOW',
          'ABOVE_OR_BELOW',
          'AND',
          'OR',
          'NOT',
          'LIKE',
          'ASC',
          'DESC',
          'NULLS FIRST',
          'NULLS LAST',
          'FOR',
          'UPDATE',
          'TRACKING',
          'VIEWSTAT',
        ],
        operators: ['=', '!=', '<', '>', '<=', '>=', '<>'],
        tokenizer: {
          root: [
            { include: '@whitespace' },
            { include: '@numbers' },
            { include: '@strings' },
            { include: '@complexIdentifiers' },
            [/[;,.]/, 'delimiter'],
            [/[(){}\[\]]/, '@brackets'], // eslint-disable-line no-useless-escape
            [
              /[\w@#$]+/,
              {
                cases: {
                  '@keywords': 'keyword',
                  '@operators': 'operator',
                  '@default': 'identifier',
                },
              },
            ],
            [/[<>=!%&+\-*/|~^]/, 'operator'],
          ],
          whitespace: [[/\s+/, 'white']],
          numbers: [
            [/0[xX][0-9a-fA-F]*/, 'number'],
            [/[$][+-]*\d*(\.\d*)?/, 'number'],
            [/((\d+(\.\d*)?)|(\.\d+))([eE][-+]?\d+)?/, 'number'],
          ],
          strings: [
            [/'/, { token: 'string.quote', bracket: '@open', next: '@string' }],
            [/{/, { token: 'string.quote', bracket: '@open', next: '@bracedString' }],
          ],
          string: [
            [/[^\\']+/, 'string'],
            [/\\./, 'string.escape'],
            [/'/, { token: 'string.quote', bracket: '@close', next: '@pop' }],
          ],
          bracedString: [
            [/[^}]+/, 'string'],
            [/}/, { token: 'string.quote', bracket: '@close', next: '@pop' }],
          ],
          complexIdentifiers: [[/`/, { token: 'identifier.quote', bracket: '@open', next: '@quotedIdentifier' }]],
          quotedIdentifier: [
            [/[^`]+/, 'identifier'],
            [/`/, { token: 'identifier.quote', bracket: '@close', next: '@pop' }],
          ],
        },
      })
    }

    await nextTick()

    if (!editorContainer.value) return

    // Create the editor
    monacoEditor = monaco?.editor.create(editorContainer.value, {
      value: props.modelValue,
      language: props.language,
      theme: 'vs',
      automaticLayout: true,
      minimap: { enabled: false },
      scrollBeyondLastLine: false,
      fontSize: 14,
      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
      lineNumbers: 'on',
      roundedSelection: false,
      scrollbar: {
        vertical: 'auto',
        horizontal: 'auto',
        useShadows: false,
        verticalHasArrows: false,
        horizontalHasArrows: false,
      },
      overviewRulerBorder: false,
      readOnly: props.readonly,
      wordWrap: 'on',
      wrappingIndent: 'indent',
      formatOnPaste: true,
      formatOnType: true,
      autoIndent: 'advanced',
      tabSize: 2,
      insertSpaces: true,
      folding: true,
      foldingStrategy: 'indentation',
      showFoldingControls: 'mouseover',
      bracketPairColorization: {
        enabled: true,
      },
      guides: {
        bracketPairs: true,
        indentation: true,
      },
    })

    // Apply theme styling to Monaco
    const editorElement = editorContainer.value?.querySelector('.monaco-editor') as HTMLElement
    if (editorElement) {
      editorElement.style.backgroundColor = themeVars.value.base
      editorElement.style.color = themeVars.value.text
    }

    // Handle value changes
    monacoEditor?.onDidChangeModelContent(() => {
      const value = monacoEditor?.getValue()
      emit('update:modelValue', value || '')
    })

    // Handle focus/blur events
    monacoEditor?.onDidFocusEditorText(() => {
      emit('focus')
    })

    monacoEditor?.onDidBlurEditorText(() => {
      emit('blur')
    })

    // Add save command (Ctrl+S)
    if (monacoEditor && monaco) {
      monacoEditor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
        emit('save')
      })
    }

    isEditorLoaded.value = true
  } catch (error) {
    console.error('Failed to load Monaco Editor:', error)
  }
}

// Watch for external value changes
watch(
  () => props.modelValue,
  (newValue) => {
    if (monacoEditor && monacoEditor.getValue() !== newValue) {
      monacoEditor.setValue(newValue || '')
    }
  },
)

// Watch for readonly changes
watch(
  () => props.readonly,
  (newReadonly) => {
    if (monacoEditor) {
      monacoEditor.updateOptions({ readOnly: newReadonly })
    }
  },
)

// Watch for language changes
watch(
  () => props.language,
  (newLanguage) => {
    if (monacoEditor && monaco) {
      const model = monacoEditor.getModel()
      if (model) {
        monaco.editor.setModelLanguage(model, newLanguage)
      }
    }
  },
)

onMounted(() => {
  loadMonaco()
})

onUnmounted(() => {
  if (monacoEditor) {
    monacoEditor.dispose()
  }
})

// Expose methods for parent component
defineExpose({
  focus: () => monacoEditor?.focus(),
  setValue: (value: string) => monacoEditor?.setValue(value || ''),
  getValue: () => monacoEditor?.getValue() || '',
  formatDocument: () => monacoEditor?.getAction('editor.action.formatDocument')?.run(),
})
</script>

<style lang="scss" scoped>
/* Monaco editor theme integration */
:deep(.monaco-editor .margin) {
  background-color: v-bind('themeVars.base');
}

:deep(.monaco-editor-background) {
  background-color: v-bind('themeVars.base');
}

:deep(.monaco-scrollable-element > .scrollbar > .scroller) {
  background: v-bind('themeVars.primary4');
}

:deep(.monaco-scrollable-element > .scrollbar > .scroller:hover) {
  background: v-bind('themeVars.primary3');
}
</style>
